import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AudioPlayer } from '@/components/AudioPlayer/AudioPlayer';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { EntityDetail } from '@/components/EntityDetail/EntityDetail';
import { EntityTypeBadge } from '@/components/EntityTypeBadge/EntityTypeBadge';
import { ExtractionGraph } from '@/components/ExtractionGraph/ExtractionGraph';
import { ExtractionStats } from '@/components/ExtractionStats/ExtractionStats';
import { Segmented } from '@/components/Segmented/Segmented';
import { SpeakerBadge } from '@/components/SpeakerBadge/SpeakerBadge';
import { Spinner } from '@/components/Spinner/Spinner';
import { TimeSlice } from '@/components/TimeSlice/TimeSlice';
import { TranscriptView } from '@/components/TranscriptView/TranscriptView';
import { useMediaQuery } from '@/lib/useMediaQuery';
import {
  categoryOf,
  entityLabel,
  fieldRows,
  humanize,
  relationshipsOf,
  toGraph,
  type Extraction,
  type TimeSpan,
} from '@/papermill/extraction';
import { loadTranscript } from '@/papermill/extraction-cache';
import {
  formatDuration,
  marksOf,
  sliceOf,
  speakerKey,
  speakersOf,
  type Transcript,
} from '@/papermill/transcript';

import styles from './TranscriptJobContainer.module.css';
import { useAudioPlayback, type Playback } from './useAudioPlayback';
import { useFitToScreen } from './useFitToScreen';

/** At this width the graph and the transcript sit side by side. */
const WIDE = '(min-width: 1100px)';
const PHONE = '(max-width: 640px)';

type View = 'transcript' | 'graph';

export interface TranscriptJobContainerProps {
  jobId: string;
  transcriptUrl: string | null;
  audioUrl: string | null;
  extraction: Extraction | null;
  extractionFailed: boolean;
  /** Arcade's measured length, until the transcript or the recording says otherwise. */
  durationMs: number | null;
  tookSeconds: number | null;
  onRetry: () => void;
}

type Loaded =
  { id: string; url: string; transcript: Transcript } | { id: string; url: string; failed: true };

/** A finished recording: the player, the transcript lit as it plays, the graph, and each value's moment. */
export function TranscriptJobContainer({
  jobId,
  transcriptUrl,
  audioUrl,
  extraction,
  extractionFailed,
  durationMs: measuredMs,
  tookSeconds,
  onRetry,
}: TranscriptJobContainerProps) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [selected, setSelected] = useState<{ id: string; field?: string } | null>(null);
  const [view, setView] = useState<View>('transcript');
  const wide = useMediaQuery(WIDE);
  const phone = useMediaQuery(PHONE);
  const slotRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const slotHeight = useFitToScreen(slotRef, playerRef, phone);

  useEffect(() => {
    if (!transcriptUrl) {
      return;
    }
    let cancelled = false;
    loadTranscript(jobId, transcriptUrl)
      .then((transcript) => !cancelled && setLoaded({ id: jobId, url: transcriptUrl, transcript }))
      .catch(() => !cancelled && setLoaded({ id: jobId, url: transcriptUrl, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [jobId, transcriptUrl]);

  const transcript =
    loaded && loaded.id === jobId && 'transcript' in loaded ? loaded.transcript : null;
  const transcriptFailed = Boolean(loaded && loaded.url === transcriptUrl && 'failed' in loaded);
  const playback = useAudioPlayback(audioUrl, transcript?.duration_ms ?? measuredMs ?? 0);
  const { stop } = playback;

  const graph = useMemo(() => (extraction ? toGraph(extraction) : null), [extraction]);
  const speakers = useMemo(
    () => (transcript ? speakersOf(transcript, extraction) : []),
    [transcript, extraction],
  );
  const marks = useMemo(
    () => (transcript ? marksOf(transcript, extraction) : undefined),
    [transcript, extraction],
  );
  const turns = useMemo(() => {
    const slots = new Map(speakers.map((speaker) => [speaker.key, speaker.slot]));
    return (transcript?.segments ?? []).map((segment) => ({
      startMs: segment.start_ms,
      endMs: segment.end_ms,
      slot: slots.get(speakerKey(segment.track_id, segment.speaker)) ?? 1,
    }));
  }, [transcript, speakers]);
  const markers = useMemo(
    () =>
      (extraction?.entities ?? [])
        .filter((entity) => !entity.discarded)
        .flatMap((entity) =>
          Object.entries(entity.fields ?? {})
            .filter(([, field]) => field.span)
            .map(([key, field]) => ({
              ms: field.span!.start_ms,
              category: categoryOf(entity.type),
              label: `${humanize(key)}: ${field.corrected_value || field.value}`,
            })),
        ),
    [extraction],
  );

  const openValue = useCallback((id: string, field?: string) => setSelected({ id, field }), []);
  const closeDialog = useCallback(() => {
    stop();
    setSelected(null);
  }, [stop]);

  if (!transcript && !transcriptFailed) {
    return transcriptUrl ? <Spinner /> : null;
  }

  const entity = extraction?.entities?.find((candidate) => candidate.id === selected?.id) ?? null;
  const heard = markers.length;
  const labelFor = (entityId: string) => {
    const other = extraction?.entities?.find((candidate) => candidate.id === entityId);
    return other ? entityLabel(other) : entityId;
  };
  const durationMs = playback.durationMs;

  const transcriptPanel = transcript ? (
    <section
      className={`${styles.panel} ${styles.transcriptPanel}`}
      aria-labelledby="transcript-heading"
    >
      <div className={styles.panelHeader}>
        <h2 id="transcript-heading" className={styles.heading}>
          Transcript
        </h2>
        <ul className={styles.speakers} aria-label="Speakers">
          {speakers.map((speaker) => (
            <li key={speaker.key}>
              <SpeakerBadge speaker={speaker} size="sm" showRole />
            </li>
          ))}
        </ul>
      </div>
      <TranscriptView
        segments={transcript.segments}
        speakers={speakers}
        currentMs={playback.slice ? -1 : playback.currentMs}
        follow={playback.playing && !playback.slice}
        marks={marks}
        onSeek={playback.status === 'ready' ? playback.playFrom : playback.seek}
        onOpenValue={openValue}
      />
    </section>
  ) : (
    <Callout tone="danger" title="Could not load the transcript">
      <Button variant="ghost" onClick={onRetry}>
        Try again
      </Button>
    </Callout>
  );

  const graphPanel = graph ? (
    <section className={`${styles.panel} ${styles.graphPanel}`} aria-labelledby="graph-heading">
      <div className={styles.panelHeader}>
        <h2 id="graph-heading" className={styles.heading}>
          What was said
        </h2>
      </div>
      {graph.nodes.length > 0 ? (
        <ExtractionGraph
          nodes={graph.nodes}
          links={graph.links}
          onSelect={openValue}
          height={wide ? 560 : 600}
          fill={phone}
        />
      ) : (
        <Callout title="Nothing to draw">
          Papermill read the transcript but found no entities in it.
        </Callout>
      )}
    </section>
  ) : extractionFailed ? (
    <Callout tone="danger" title="Could not load the extraction">
      <Button variant="ghost" onClick={onRetry}>
        Try again
      </Button>
    </Callout>
  ) : (
    <Spinner />
  );

  return (
    <section aria-label="Recording" className={styles.layout}>
      {phone ? null : (
        <ExtractionStats
          stats={[
            { label: 'Entities', value: graph?.nodes.length ?? '—' },
            { label: 'Relationships', short: 'Links', value: graph?.links.length ?? '—' },
            { label: 'Values heard', short: 'Values', value: graph ? heard : '—' },
            { label: 'Speakers', short: 'Voices', value: speakers.length || '—' },
            { label: 'Length', value: durationMs ? formatDuration(durationMs) : '—' },
            { label: 'Took', value: tookSeconds !== null ? `${Math.round(tookSeconds)}s` : '—' },
          ]}
        />
      )}

      {audioUrl ? (
        <div ref={playerRef} className={styles.player}>
          <AudioPlayer
            status={playback.status === 'idle' ? 'loading' : playback.status}
            playing={playback.playing && !playback.slice}
            currentMs={playback.currentMs}
            durationMs={durationMs}
            onToggle={playback.toggle}
            onSeek={playback.seek}
            rate={playback.rate}
            onRate={playback.setRate}
            turns={turns}
            markers={markers}
          />
        </div>
      ) : null}

      {wide ? (
        <div className={styles.split}>
          {graphPanel}
          {transcriptPanel}
        </div>
      ) : (
        <>
          <Segmented
            label="Show"
            options={[
              { value: 'transcript', label: 'Transcript' },
              { value: 'graph', label: 'Graph' },
            ]}
            value={view}
            onChange={setView}
            className={styles.tabs}
          />
          <div
            ref={slotRef}
            className={styles.slot}
            style={slotHeight ? { height: slotHeight } : undefined}
          >
            {view === 'transcript' ? transcriptPanel : graphPanel}
          </div>
        </>
      )}

      {entity && extraction ? (
        <Dialog
          title={entityLabel(entity)}
          eyebrow={
            <EntityTypeBadge typeLabel={humanize(entity.type)} category={categoryOf(entity.type)} />
          }
          onClose={closeDialog}
          size="full"
        >
          <EntityDetail
            key={`${entity.id}:${selected?.field ?? ''}`}
            medium="recording"
            initialField={selected?.field}
            fields={fieldRows(entity.fields)}
            relationships={relationshipsOf(entity.id, extraction.edges ?? []).map(
              (relationship) => ({
                label: relationship.label,
                direction: relationship.direction,
                otherId: relationship.otherId,
                otherLabel: labelFor(relationship.otherId),
              }),
            )}
            onOpenEntity={(id) => setSelected({ id })}
            renderSource={({ span }) =>
              span && transcript ? (
                <SliceSource
                  span={span}
                  transcript={transcript}
                  speakers={speakers}
                  durationMs={durationMs}
                  playback={playback}
                  canPlay={Boolean(audioUrl) && playback.status === 'ready'}
                />
              ) : (
                <p>The transcript is not available for this job.</p>
              )
            }
          />
        </Dialog>
      ) : null}
    </section>
  );
}

function SliceSource({
  span,
  transcript,
  speakers,
  durationMs,
  playback,
  canPlay,
}: {
  span: TimeSpan;
  transcript: Transcript;
  speakers: ReturnType<typeof speakersOf>;
  durationMs: number;
  playback: Playback;
  canPlay: boolean;
}) {
  const lines = useMemo(() => sliceOf(transcript, span), [transcript, span]);
  const playing =
    playback.playing &&
    playback.slice !== null &&
    playback.slice[0] === span.start_ms &&
    playback.slice[1] === span.end_ms;
  return (
    <TimeSlice
      span={span}
      lines={lines}
      speakers={speakers}
      durationMs={durationMs}
      currentMs={playback.currentMs}
      playing={playing}
      onPlay={canPlay ? () => playback.playSlice(span.start_ms, span.end_ms) : undefined}
      onStop={playback.stop}
    />
  );
}
